// Starter-catalog browser — approved merchants flip common items on/off into
// their inventory instead of typing each one. Toggling on seeds a priced item
// (editable afterwards in the inventory list); toggling off removes it.

import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '../../components/shared/Modal';
import { merchantCatalog, type CatalogGroup } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

interface Snapshot { groups: CatalogGroup[]; activated_count: number; total: number }

export function CatalogBrowser({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['merchant', 'catalog'], queryFn: merchantCatalog.list });

  const toggle = useMutation({
    mutationFn: (id: number) => merchantCatalog.toggle(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['merchant', 'catalog'] });
      const prev = qc.getQueryData<Snapshot>(['merchant', 'catalog']);
      qc.setQueryData<Snapshot>(['merchant', 'catalog'], (old) => old && ({
        ...old,
        activated_count: old.activated_count + (old.groups.flatMap((g) => g.items).find((i) => i.id === id)?.activated ? -1 : 1),
        groups: old.groups.map((g) => ({ ...g, items: g.items.map((it) => it.id === id ? { ...it, activated: !it.activated } : it) })),
      }));
      return { prev };
    },
    onError: (e: Error, _id, ctx) => { if (ctx?.prev) qc.setQueryData(['merchant', 'catalog'], ctx.prev); toast.error(e.message || 'Could not update'); },
    onSettled: () => { qc.invalidateQueries({ queryKey: ['merchant', 'items'] }); },
  });

  return (
    <Modal open onClose={onClose} width="lg" title="Add from catalog"
      subtitle={q.data ? `${q.data.activated_count} of ${q.data.total} common items in your store — toggle to stock them.` : 'Toggle common items into your store.'}>
      {q.isLoading ? (
        <div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading catalog…</div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 space-y-5">
          {(q.data?.groups ?? []).map((g) => (
            <div key={g.slug}>
              <p className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{g.label}</p>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                {g.items.map((it) => (
                  <div key={it.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-slate-900 truncate">{it.name}</p>
                      <p className="text-[11.5px] text-slate-400">{it.unit} · suggested {formatHtgMinor(it.default_price_htg_minor)}</p>
                    </div>
                    <button
                      onClick={() => toggle.mutate(it.id)}
                      aria-pressed={it.activated}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${it.activated ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                      {it.activated ? <><Check className="w-3.5 h-3.5" /> In store</> : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-slate-400">Added items use the suggested price — edit them anytime in your inventory.</p>
    </Modal>
  );
}
